import { CloudUploadOutlined, VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material'
import { Box, Grid, IconButton, InputAdornment, OutlinedTextFieldProps, TextField } from '@mui/material'
import React, { ChangeEvent, Dispatch, SetStateAction, useRef, useState } from 'react'
import { Control, Controller, RegisterOptions, UseFormRegister, UseFormSetValue } from 'react-hook-form'

import { PatternFormat } from 'react-number-format'

interface Sizing {
  xs: number
  sm: number
  md: number
  lg: number
  xl: number
}

interface IFormInput extends Omit<OutlinedTextFieldProps, 'variant'> {
  disabled: boolean
  error: boolean
  errorMessage: string | undefined
  label: string
  maxLength?: number
  sizing: Sizing
  register?: UseFormRegister<any>
  rules?: RegisterOptions
  fileAccept?: string
  name: string
  onSetValue?: UseFormSetValue<any>
  fileState?: Dispatch<SetStateAction<any[]>>
  control?: Control<any, object>
}

const FormInput: React.FC<IFormInput> = ({
  disabled,
  error,
  errorMessage,
  label,
  sizing,
  maxLength = 256,
  register,
  name,
  fileAccept,
  fileState,
  onSetValue,
  type,
  rules = {},
  children,
  control,
  ...FormInputProps
}) => {
  const [isPasswordHidden, setIsPasswordHidden] = useState<boolean>(true)
  const fileRef = useRef<any>()

  let actualType = type

  if (type === 'password') {
    actualType = isPasswordHidden ? 'password' : 'text'
  } else if (type === 'file') {
    actualType = 'text'
  } else if (type === 'tel') {
    actualType = 'tel'
  } else if (type === 'color') {
    actualType = 'color'
  }

  const handleFileSelected = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = Array.from(e.target.files!)

    onSetValue!(name, e.target.files![0].name)
    fileState!(files)
  }

  const handleUploadFileClick = (componentRef: any) => {
    componentRef.current.click()
  }

  const EndAdornment = () => {
    if (type === 'file') {
      return (
        <InputAdornment position='end'>
          <IconButton
            edge='end'
            onClick={(e) => handleUploadFileClick(fileRef)}
          >
            <CloudUploadOutlined />
          </IconButton>
          <input
            type='file'
            hidden
            ref={fileRef}
            accept={fileAccept}
            onChange={handleFileSelected}
          />
        </InputAdornment>
      )
    } else if (type === 'password') {
      return (
        <IconButton onClick={() => setIsPasswordHidden(!isPasswordHidden)}>
          {isPasswordHidden ? <VisibilityOffOutlined color='inherit' /> : <VisibilityOutlined />}
        </IconButton>
      )
    } else {
      return <></>
    }
  }

  if (actualType === 'color') {
    return (
      <Grid
        item
        xs={sizing.xs}
        sm={sizing.sm}
        md={sizing.md}
        lg={sizing.lg}
        xl={sizing.xl}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { name, onBlur, onChange, ref, value } }) => (
            <TextField
              helperText={errorMessage}
              label={label}
              margin='dense'
              size='small'
              fullWidth={true}
              id={name}
              error={error}
              disabled={disabled}
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value.toUpperCase().replace('#', '')}
              inputProps={{ maxLength: 6 }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <Box
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                  >
                    <input
                      type='color'
                      value={`#${value.toUpperCase()}`}
                      onChange={(e) => onChange(e.target.value.replace('#', ''))}
                      style={{ marginRight: '16px', padding: 0, border: 0, outline: 0 }}
                    />
                    #
                  </Box>
                ),
              }}
            />
          )}
        />
      </Grid>
    )
  }

  if (actualType === 'tel') {
    return (
      <Grid
        item
        xs={sizing.xs}
        sm={sizing.sm}
        md={sizing.md}
        lg={sizing.lg}
        xl={sizing.xl}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { name, onBlur, onChange, ref, value } }) => (
            <PatternFormat
              customInput={TextField}
              id={name}
              error={error}
              format='(##) #####-####'
              mask=' '
              placeholder='(99) 99999-9999'
              helperText={errorMessage}
              label={label}
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{ shrink: true }}
              disabled={disabled}
              inputRef={ref}
              onBlur={onBlur}
              onChange={onChange}
              value={value}
            />
          )}
        />
      </Grid>
    )
  }

  return (
    <Grid
      item
      xs={sizing.xs}
      sm={sizing.sm}
      md={sizing.md}
      lg={sizing.lg}
      xl={sizing.xl}
    >
      <TextField
        id={name}
        error={error}
        helperText={errorMessage}
        label={label}
        variant='outlined'
        margin='dense'
        size='small'
        fullWidth={true}
        type={actualType}
        InputLabelProps={{ shrink: true }}
        disabled={disabled}
        inputProps={{ maxLength: maxLength }}
        InputProps={{ endAdornment: <EndAdornment />, sx: { paddingRight: '16px' } }}
        {...register!(name, rules)}
        {...FormInputProps}
      />
    </Grid>
  )
}

export default FormInput
